/**
 * Named hourly labor-rate catalog on the workshop pricing page (#2891).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useCreateLaborRate,
  useDeleteLaborRate,
  useLaborRates,
  useUpdateLaborRate,
  type LaborRate,
} from '@/hooks/useLaborRates';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { FormDialog } from '@/components/common/FormDialog';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { eurosToCents } from '@/utils/money';
import { DEFAULT_VAT_PERCENT, netToGrossCents } from '@/utils/vat';

function displayGrossInput(rate: LaborRate): string {
  const gross =
    rate.rateGrossCents != null && rate.rateGrossCents > 0
      ? rate.rateGrossCents
      : netToGrossCents(rate.rateCents, DEFAULT_VAT_PERCENT);
  return gross > 0 ? (gross / 100).toFixed(2) : '';
}

export function LaborRatesSettings() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManage = hasPermission('settings.edit_workshop');
  const { data } = useLaborRates({ isActive: true });
  const rates = data?.data ?? [];
  const createRate = useCreateLaborRate();
  const updateRate = useUpdateLaborRate();
  const deleteRate = useDeleteLaborRate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LaborRate | null>(null);
  const [name, setName] = useState('');
  const [rateInput, setRateInput] = useState('');
  const [pendingDelete, setPendingDelete] = useState<LaborRate | null>(null);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setRateInput('');
    setDialogOpen(true);
  };

  const openEdit = (rate: LaborRate) => {
    setEditing(rate);
    setName(rate.name);
    setRateInput(displayGrossInput(rate));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    const cents = eurosToCents(rateInput.replace(',', '.'));
    if (!trimmed) {
      notify.error(t('laborRates.nameRequired'));
      return;
    }
    if (cents == null || cents <= 0) {
      notify.error(t('laborRates.rateRequired'));
      return;
    }
    try {
      if (editing) {
        await updateRate.mutateAsync({
          id: editing.id,
          data: { name: trimmed, rateGrossCents: cents },
        });
      } else {
        await createRate.mutateAsync({
          name: trimmed,
          rateGrossCents: cents,
          isDefault: rates.length === 0,
        });
      }
      notify.success(t('laborRates.saved'));
      setDialogOpen(false);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('laborRates.saveFailed') }));
    }
  };

  const handleSetDefault = async (rate: LaborRate) => {
    try {
      await updateRate.mutateAsync({ id: rate.id, data: { isDefault: true } });
      notify.success(t('laborRates.saved'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('laborRates.saveFailed') }));
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteRate.mutateAsync(pendingDelete.id);
      notify.success(t('laborRates.deleted'), t('laborRates.deletedDesc', { name: pendingDelete.name }));
      setPendingDelete(null);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('laborRates.deleteFailed') }));
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('laborRates.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('laborRates.subtitle')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <Stack spacing={1.5}>
          {rates.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
              {t('laborRates.empty')}
            </Typography>
          )}
          {rates.map((rate) => (
            <Box
              key={rate.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 0.5,
              }}
            >
              <Typography
                sx={{ flex: 1, cursor: canManage ? 'pointer' : 'default' }}
                onClick={canManage ? () => openEdit(rate) : undefined}
              >
                {rate.name}
              </Typography>
              <Typography color="text.secondary">
                {(
                  (rate.rateGrossCents != null && rate.rateGrossCents > 0
                    ? rate.rateGrossCents
                    : netToGrossCents(rate.rateCents, DEFAULT_VAT_PERCENT)) / 100
                ).toFixed(2)}{' '}
                € / {t('hour')}
              </Typography>
              {rate.isDefault ? (
                <Chip label={t('laborRates.default')} size="small" color="primary" />
              ) : canManage ? (
                <Tooltip title={t('laborRates.setDefault')}>
                  <Chip
                    label={t('laborRates.default')}
                    size="small"
                    variant="outlined"
                    onClick={() => void handleSetDefault(rate)}
                  />
                </Tooltip>
              ) : null}
              {canManage && (
                <Tooltip title={tc('delete')}>
                  <IconButton
                    size="small"
                    onClick={() => setPendingDelete(rate)}
                    aria-label={tc('delete')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          {canManage && (
            <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={openCreate}>
              {t('laborRates.add')}
            </Button>
          )}
        </Stack>
      </Paper>

      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('laborRates.edit') : t('laborRates.add')}
        isDirty={
          name.trim() !== (editing?.name ?? '').trim()
          || rateInput.trim() !== (editing ? displayGrossInput(editing) : '')
        }
        onSubmit={() => void handleSave()}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('laborRates.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            slotProps={{
              htmlInput: { 'aria-label': t('laborRates.name') },
            }}
          />
          <TextField
            label={t('laborRates.rateInclVat')}
            value={rateInput}
            onChange={(e) => setRateInput(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                endAdornment: <InputAdornment position="end">/ {t('hour')}</InputAdornment>,
              },
              htmlInput: { 'aria-label': t('laborRates.rateInclVat') },
            }}
          />
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={pendingDelete != null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void handleDelete()}
        title={t('laborRates.deleteConfirmTitle')}
        message={t('laborRates.deleteConfirm', { name: pendingDelete?.name ?? '' })}
      />
    </>
  );
}
